import { t } from '../i18n.js';
import { exitAndRemove, reducedMotion } from './motion.js';
import { buildActionRow, partitionDestructive, skipsInitialFocus, isActionRowDisabled } from './action-row.js';

const SHEET_DISMISS_PX = 120;
const SHEET_DISMISS_RATIO = 0.30;

/**
 * Returns the minimum drag distance (px) required to dismiss the sheet.
 * Pure function — safe to call in Node tests without a live DOM.
 * @param {number} panelHeight — panel.offsetHeight (0 in jsdom)
 * @returns {number}
 */
export function dismissThreshold(panelHeight) {
  return Math.min(SHEET_DISMISS_PX, panelHeight * SHEET_DISMISS_RATIO);
}

let currentClose = null;

function focusFirstFocusable(container) {
  const candidates = container.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
  );
  for (const el of candidates) {
    // Destructive rows sort last already (partitionDestructive), so this only
    // changes anything when a row set is destructive-only — the case where
    // opening focus would otherwise land on the one row nobody asked to fire.
    if (!el.disabled && !isActionRowDisabled(el) && !skipsInitialFocus(el)) {
      el.focus();
      return;
    }
  }
  container.focus();
}

/**
 * Open a bottom sheet of choice or action rows.
 *
 * @param {object} opts
 * @param {string} [opts.title]
 * @param {Array<
 *   | { kind: 'check',  label: string, checked?: boolean,  onChange: (next: boolean) => void }
 *   | { kind: 'radio',  label: string, selected?: boolean, onChange: () => void }
 *   | { kind: 'action', label: string, id?: string, destructive?: boolean, icon?: string,
 *       dataRole?: string, onSelect: () => void }
 * >} opts.rows
 *   - `kind: 'action'`: full-width tap target. Invokes `onSelect()` synchronously, then closes
 *     the sheet. `destructive: true` re-orders the row to the bottom under a thin divider and
 *     applies red label styling. `icon` (optional) renders a leading glyph in the indicator
 *     slot. `dataRole` (optional) overrides the default `data-role="sheet-row"` on the row
 *     button; the consumer's value replaces the default entirely.
 * @param {HTMLElement|null} [opts.footer]
 * @param {function} [opts.onDismiss]
 * @returns {{ close(): void }}
 */
export function bottomSheet(opts) {
  const { title, rows = [], footer = null, onDismiss = null } = opts;

  const kinds = new Set(rows.map((r) => r.kind));
  const mixed = kinds.size > 1;
  if (mixed) {
    if (process.env.NODE_ENV !== 'production') {
      throw new Error('bottomSheet: mixed row kinds in one call are not supported.');
    }
    // In production: render only rows matching the first kind
  }
  const firstKind = rows[0]?.kind;
  const visibleRows = mixed ? rows.filter((r) => r.kind === firstKind) : rows;

  // Single-open invariant: close prior sheet first
  if (currentClose) {
    currentClose();
  }

  // Capture focus target for restoration
  const priorFocus = document.activeElement;

  // Z-index escalation: probe for open modal
  const modalOpen = document.querySelector('[data-modal-open="true"]') !== null;
  const zClass = modalOpen ? 'z-modal-over' : 'z-50';

  // --- Build DOM ---

  // Scrim (sits behind the panel, but same z-level via DOM order)
  const scrim = document.createElement('button');
  scrim.type = 'button';
  scrim.setAttribute('data-role', 'sheet-scrim');
  scrim.setAttribute('aria-label', t('actions.close_sheet'));
  scrim.className = `fixed inset-0 bg-black/40 backdrop-blur-sm ${zClass} anim-fade-in`;
  // Mirror of the modal's `data-modal-open` probe, in the other direction:
  // modal.js reads this to keep its document-level Escape/Enter handlers
  // quiet while a sheet is stacked over it. Without it a single Escape
  // closes both, and the discard guard dismisses the form it is guarding.
  scrim.dataset.sheetOpen = 'true';

  // Panel
  const panel = document.createElement('div');
  panel.setAttribute('data-role', 'sheet-panel');
  panel.setAttribute('tabindex', '-1');
  panel.className = [
    'fixed bottom-0 left-0 right-0 max-w-md mx-auto',
    // Fixed chrome — .glass-panel carries the dark "Depth & glass" blur
    // treatment (D2); scrolling list cards never get this class.
    'glass-panel',
    'rounded-t-2xl',
    'max-h-sheet-panel overflow-y-auto overscroll-contain',
    'px-4 pb-[max(1.375rem,env(safe-area-inset-bottom))]',
    'pt-2.5',
    zClass,
    'anim-sheet-up',
  ].join(' ');

  // Grab handle (decorative)
  const handle = document.createElement('div');
  handle.setAttribute('aria-hidden', 'true');
  handle.className = 'mx-auto mb-3 h-1 w-10 rounded-full bg-chip-neutral dark:bg-chip-neutral-dark';
  panel.append(handle);

  // Title
  if (title) {
    const titleEl = document.createElement('p');
    titleEl.className = 'text-xs font-semibold uppercase tracking-wide text-ink-muted mb-2';
    titleEl.textContent = title;
    panel.append(titleEl);
  }

  // Rows
  const rowList = document.createElement('ul');
  rowList.className = 'divide-y divide-border dark:divide-border-dark';

  // Stable-partition: non-destructive rows first, destructive rows last
  const nonDestructive = visibleRows.filter((r) => !r.destructive);
  const partitionedRows = partitionDestructive(visibleRows);

  const rowStates = partitionedRows.map((rowDef) => ({ ...rowDef }));

  rowStates.forEach((rowState, idx) => {
    const isDestructive = rowState.destructive === true;
    const isFirstDestructive = isDestructive && idx === nonDestructive.length;

    if (rowState.kind === 'action') {
      // `icon` accepts either a legacy glyph string or a brand-icon SVG element
      // (ui-visual-refresh D3) — string rows keep rendering unchanged. Duck-typed
      // rather than `instanceof Node` since some test environments don't expose
      // the DOM `Node` constructor as a bare global.
      const { li, btn } = buildActionRow({
        label: rowState.label,
        id: rowState.id,
        destructive: isDestructive,
        isFirstDestructive,
        disabled: rowState.disabled === true,
        dataRole: rowState.dataRole,
        defaultDataRole: isDestructive ? 'row-danger' : 'sheet-row',
        icon: rowState.icon,
      });
      rowList.append(li);

      btn.addEventListener('click', () => {
        // A disabled row is aria-disabled, not natively disabled (see
        // buildActionRow), so it stays a real, clickable button — this guard
        // is what actually stops the click, same as rowActions.js's menu.
        if (rowState.disabled === true) return;
        rowState.onSelect();
        closeSheet();
      });
      return;
    }

    // `check`/`radio` rows never declare `destructive` (see the type union
    // above) — no caller sets it, so this row never partitions under a
    // divider or takes the danger label.
    const li = document.createElement('li');

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'flex w-full items-center gap-3 min-h-touch py-2 text-sm text-left';
    btn.setAttribute('data-role', 'sheet-row');

    // Consumer-supplied dataRole overrides the default entirely
    if (rowState.dataRole) {
      btn.dataset.role = rowState.dataRole;
    }

    const indicator = document.createElement('span');
    indicator.setAttribute('aria-hidden', 'true');

    const labelEl = document.createElement('span');
    labelEl.textContent = rowState.label;

    function updateIndicator() {
      if (rowState.kind === 'check') {
        indicator.textContent = rowState.checked ? '☑' : '☐';
      } else {
        indicator.textContent = rowState.selected ? '◉' : '○';
      }
    }
    updateIndicator();

    btn.append(indicator, labelEl);
    li.append(btn);
    rowList.append(li);

    btn.addEventListener('click', () => {
      if (rowState.kind === 'check') {
        rowState.checked = !rowState.checked;
        updateIndicator();
        rowState.onChange(rowState.checked);
        // Sheet stays open
      } else {
        // Radio: clear other selected rows
        rowStates.forEach((other, otherIdx) => {
          if (otherIdx !== idx) {
            other.selected = false;
            const otherBtn = rowList.querySelectorAll('[data-role="sheet-row"]')[otherIdx];
            if (otherBtn) {
              const otherIndicator = otherBtn.querySelector('[aria-hidden]');
              if (otherIndicator) otherIndicator.textContent = '○';
            }
          }
        });
        rowState.selected = true;
        updateIndicator();
        rowState.onChange();
        // Sheet closes
        closeSheet();
      }
    });
  });

  panel.append(rowList);

  // Footer slot
  if (footer) {
    panel.append(footer);
  }

  // Body scroll lock with ownership chaining
  if (!document.body.dataset.bodyLockOwner) {
    document.body.classList.add('overflow-hidden');
    document.body.dataset.bodyLockOwner = 'sheet';
  }

  // Esc key listener
  function onKeydown(e) {
    if (e.key === 'Escape') {
      closeSheet();
    }
  }
  document.addEventListener('keydown', onKeydown);

  scrim.addEventListener('click', closeSheet);

  // Focus trap: intercept Tab inside panel
  panel.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    const focusable = Array.from(
      panel.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'),
    ).filter((el) => !el.disabled);
    if (focusable.length === 0) {
      // Nothing to trap between — without this, Tab falls through to
      // whatever is next in the document, out of a dialog the user
      // believes is modal.
      e.preventDefault();
      panel.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (document.activeElement === panel) {
      // Opening focus fell through to the panel itself (skipsInitialFocus /
      // isActionRowDisabled left every candidate ineligible). The panel is
      // not itself in `focusable`, so neither boundary branch below can
      // ever match it — without this, Tab/Shift+Tab escapes to whatever is
      // tabbable just outside the dialog.
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
      return;
    }
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  // --- Drag-to-dismiss (GMP-01, GMP-04, GMP-07, GMP-08) ---

  let dragStartY = 0;
  let dragDelta = 0;
  let dragActive = false;

  function setChildrenPointerEvents(value) {
    for (const child of panel.children) {
      if (child !== handle) {
        child.style.pointerEvents = value;
      }
    }
  }

  function onDragMove(e) {
    if (!dragActive) return;
    dragDelta = Math.max(0, e.clientY - dragStartY);
    if (!reducedMotion()) {
      panel.style.transform = `translateY(${dragDelta}px)`;
    }
  }

  function cancelDragListeners() {
    // Shared teardown so mid-drag closeSheet() and normal pointer-up/cancel
    // both reach a single cleanup path without leaving stale document listeners.
    document.removeEventListener('pointermove', onDragMove);
    document.removeEventListener('pointerup', onDragEnd);
    document.removeEventListener('pointercancel', onDragEnd);
  }

  function onDragEnd() {
    if (!dragActive) return;
    const delta = dragDelta;
    dragActive = false;
    cancelDragListeners();
    panel.style.touchAction = '';
    setChildrenPointerEvents('');

    if (delta >= dismissThreshold(panel.offsetHeight)) {
      // Past threshold: dismiss through the existing close path so focus
      // restoration, scroll-lock release, onDismiss, and currentClose
      // cleanup all execute identically to a scrim-tap dismiss.
      closeSheet();
    } else if (!reducedMotion()) {
      // Below threshold: spring back to resting position; clear inline
      // styles once the transition settles so the next drag starts clean.
      panel.style.transition = 'transform 0.25s cubic-bezier(0.2,0.8,0.3,1)';
      panel.style.transform = 'translateY(0)';
      panel.addEventListener('transitionend', () => {
        panel.style.transition = '';
        panel.style.transform = '';
      }, { once: true });
    }
    // Under reduced motion: no transform was ever applied, nothing to do.
  }

  handle.addEventListener('pointerdown', (e) => {
    dragStartY = e.clientY;
    dragDelta = 0;
    dragActive = true;

    // Remove entrance animation so it doesn't fight the live transform (GMP-01).
    panel.classList.remove('anim-sheet-up');

    // Prevent touch scroll interference during drag (GMP-04).
    panel.style.touchAction = 'none';

    // Prevent accidental row taps while dragging (GMP-01).
    setChildrenPointerEvents('none');

    // Capture pointer to the handle so move/up fire even if pointer leaves (if supported).
    if (typeof handle.setPointerCapture === 'function') {
      try { handle.setPointerCapture(e.pointerId); } catch {}
    }

    // Register move/up/cancel on document so tracking works outside the handle bounds.
    document.addEventListener('pointermove', onDragMove);
    document.addEventListener('pointerup', onDragEnd);
    document.addEventListener('pointercancel', onDragEnd);
    // Focus is intentionally NOT moved here (GMP-08).
  });

  // Mount to DOM
  document.body.append(scrim);
  document.body.append(panel);

  // Move focus into the panel
  focusFirstFocusable(panel);

  function closeSheet() {
    if (currentClose !== closeSheet) return;
    currentClose = null;

    document.removeEventListener('keydown', onKeydown);

    // If a drag is active when another close path fires (Esc, scrim tap, row
    // select), remove the document-level pointer listeners so they don't linger
    // past the sheet's lifetime.
    if (dragActive) {
      dragActive = false;
      cancelDragListeners();
    }

    exitAndRemove(scrim, 'anim-fade-out');
    exitAndRemove(panel, 'anim-sheet-down');

    // Release body scroll lock only if we own it
    if (document.body.dataset.bodyLockOwner === 'sheet') {
      document.body.classList.remove('overflow-hidden');
      delete document.body.dataset.bodyLockOwner;
    }

    // Restore focus
    if (priorFocus && typeof priorFocus.focus === 'function') {
      priorFocus.focus();
    }

    if (onDismiss) {
      try { onDismiss(); } catch {}
    }
  }

  currentClose = closeSheet;

  return { close: closeSheet };
}
